import { useState, useEffect } from 'react';
import { commissionsAPI } from '../utils/api';
import { formatCurrency, formatDate, formatNumber } from '../utils/formatters';
import Loading from '../components/Loading';
import ErrorMessage from '../components/ErrorMessage';
import Dropdown from '../components/Dropdown';
import DatePicker from '../components/DatePicker';
import {
  FiDollarSign,
  FiShoppingCart,
  FiTrendingUp,
  FiChevronDown,
  FiChevronUp,
  FiCalendar,
  FiRotateCcw
} from 'react-icons/fi';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const Commissions = () => {
  const [commissionsData, setCommissionsData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [partnerCommissions, setPartnerCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSalesperson, setExpandedSalesperson] = useState(null);
  const [dateRange, setDateRange] = useState('all'); // 'all', 'thisMonth', 'lastMonth', 'custom'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const fetchCommissions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Calculate date range based on selection
      let params = {};

      if (dateRange === 'thisMonth') {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        params.startDate = Math.floor(startOfMonth.getTime() / 1000);
        params.endDate = Math.floor(Date.now() / 1000);
      } else if (dateRange === 'lastMonth') {
        const now = new Date();
        const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        params.startDate = Math.floor(startOfLastMonth.getTime() / 1000);
        params.endDate = Math.floor(endOfLastMonth.getTime() / 1000);
      } else if (dateRange === 'custom' && customStartDate && customEndDate) {
        params.startDate = Math.floor(new Date(customStartDate).getTime() / 1000);
        params.endDate = Math.floor(new Date(customEndDate + ' 23:59:59').getTime() / 1000);
      }

      const response = await commissionsAPI.getAll(params);
      setCommissionsData(response.data.data);
      setSummary(response.data.summary);
      setPartnerCommissions(response.data.partnerCommissions || []);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load commissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch if:
    // 1. Not using custom range (preset selected), OR
    // 2. Using custom range AND both dates are selected
    if (dateRange !== 'custom' || (customStartDate && customEndDate)) {
      fetchCommissions();
    }
  }, [dateRange, customStartDate, customEndDate]);

  const toggleExpand = (name) => {
    setExpandedSalesperson(expandedSalesperson === name ? null : name);
  };

  if (loading) return <Loading message="Loading commissions..." />;
  if (error) return <ErrorMessage message={error} onRetry={fetchCommissions} />;

  // Prepare chart data (using net values for accurate representation)
  const chartData = commissionsData.slice(0, 10).map(sp => ({
    name: sp.name.length > 20 ? sp.name.substring(0, 20) + '...' : sp.name,
    commission: (sp.netCommission ?? sp.totalCommission) / 100,
    sales: (sp.netSales ?? sp.totalSales) / 100
  }));

  const pieData = commissionsData.slice(0, 6).map(sp => ({
    name: sp.name,
    value: sp.netCommission ?? sp.totalCommission
  }));

  // Group salespeople by section
  const SECTION_ORDER = ['Closers', 'SDRs', 'CSMs', 'No Section'];
  const groupedData = SECTION_ORDER.reduce((acc, section) => {
    const sectionData = commissionsData.filter(sp => sp.section === section);
    if (sectionData.length > 0) {
      acc[section] = sectionData;
    }
    return acc;
  }, {});

  const sectionColors = {
    'Closers': { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100' },
    'SDRs': { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100' },
    'CSMs': { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-100' },
    'No Section': { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', badge: 'bg-gray-100' }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Commission Tracking</h1>
        <p className="mt-1 text-gray-600">
          Track sales team performance and commissions
        </p>
      </div>

      {/* Date Filter */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FiCalendar className="inline mr-2" />
              Date Range
            </label>
            <Dropdown
              value={dateRange}
              onChange={(val) => {
                setDateRange(val);
                if (val !== 'custom') {
                  setCustomStartDate('');
                  setCustomEndDate('');
                }
              }}
              options={[
                { value: 'all', label: 'All Time' },
                { value: 'thisMonth', label: 'This Month' },
                { value: 'lastMonth', label: 'Last Month' },
                { value: 'custom', label: 'Custom Range' }
              ]}
              className="w-48"
            />
          </div>

          {dateRange === 'custom' && (
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Dates
              </label>
              <DatePicker
                startDate={customStartDate}
                endDate={customEndDate}
                onStartDateChange={setCustomStartDate}
                onEndDateChange={setCustomEndDate}
                placeholder="Click to select date range"
                className="w-full md:w-80"
              />
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="space-y-6">
          {/* Total Commission (Final Payable) - Highlighted */}
          <div className="card bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-700">Total Commission (Final Payable)</p>
                <p className="text-3xl font-bold text-green-600">
                  {formatCurrency(summary.totalCommission || summary.netCommission, 'USD')}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  Sales: {formatCurrency(summary.netCommission, 'USD')} + Management: {formatCurrency(summary.managementCommission || 0, 'USD')}
                </p>
              </div>
              <div className="p-4 bg-green-100 rounded-lg">
                <FiDollarSign className="w-8 h-8 text-green-600" />
              </div>
            </div>
          </div>

          {/* Breakdown Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Gross Sales</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(summary.grossSales, 'USD')}
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <FiTrendingUp className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Gross Commission</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    {formatCurrency(summary.grossCommission, 'USD')}
                  </p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg">
                  <FiDollarSign className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Refunds</p>
                  <p className="text-2xl font-bold text-red-600">
                    -{formatCurrency(summary.totalRefunds || 0, 'USD')}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Commission: -{formatCurrency(summary.totalRefundCommission || 0, 'USD')}
                  </p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <FiRotateCcw className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Transactions</p>
                  <p className="text-2xl font-bold text-primary-600">
                    {formatNumber(summary.totalTransactions)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatNumber(summary.totalRefundCount || 0)} refund{(summary.totalRefundCount || 0) !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="p-3 bg-primary-50 rounded-lg">
                  <FiShoppingCart className="w-6 h-6 text-primary-600" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      {chartData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Commission Bar Chart */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Top Performers by Commission
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={100} />
                <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                <Legend />
                <Bar dataKey="commission" fill="#10b981" name="Commission" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Commission Distribution Pie Chart */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Commission Distribution
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value, 'USD')} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Management Commissions */}
      {partnerCommissions.length > 0 && summary && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Management
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Based on net revenue: {formatCurrency(summary.netSales, 'USD')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {partnerCommissions.map((partner) => (
              <div
                key={partner.name}
                className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-purple-900">{partner.name}</p>
                    <p className="text-sm text-purple-600">{partner.ratePercent} of net revenue</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-purple-700">
                      {formatCurrency(partner.commission, 'USD')}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Commissions by Section */}
      {commissionsData.length === 0 ? (
        <div className="card">
          <p className="text-center text-gray-500 py-8">
            No commission data available
          </p>
        </div>
      ) : (
        SECTION_ORDER.map(section => {
          const sectionSalespeople = groupedData[section];
          if (!sectionSalespeople || sectionSalespeople.length === 0) return null;

          const colors = sectionColors[section];
          const sectionTotals = sectionSalespeople.reduce((acc, sp) => ({
            sales: acc.sales + sp.totalSales,
            refunds: acc.refunds + (sp.totalRefunds || 0),
            commission: acc.commission + (sp.netCommission ?? sp.totalCommission)
          }), { sales: 0, refunds: 0, commission: 0 });

          return (
            <div key={section} className="card">
              {/* Section Header */}
              <div className={`flex items-center justify-between mb-4 pb-3 border-b ${colors.border}`}>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${colors.badge} ${colors.text}`}>
                    {section}
                  </span>
                  <span className="text-sm text-gray-500">
                    {sectionSalespeople.length} member{sectionSalespeople.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <div className="text-right hidden md:block">
                    <span className="text-gray-500">Total Sales:</span>
                    <span className="ml-2 font-semibold">{formatCurrency(sectionTotals.sales, 'USD')}</span>
                  </div>
                  {sectionTotals.refunds > 0 && (
                    <div className="text-right hidden lg:block">
                      <span className="text-gray-500">Total Refunds:</span>
                      <span className="ml-2 font-semibold text-red-600">-{formatCurrency(sectionTotals.refunds, 'USD')}</span>
                    </div>
                  )}
                  <div className="text-right">
                    <span className="text-gray-500">Total Commission:</span>
                    <span className="ml-2 font-semibold text-green-600">{formatCurrency(sectionTotals.commission, 'USD')}</span>
                  </div>
                </div>
              </div>

              {/* Section Members */}
              <div className="space-y-2">
                {sectionSalespeople.map((salesperson, index) => (
                  <div
                    key={salesperson.name}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    {/* Salesperson Summary */}
                    <div
                      className="p-4 bg-gray-50 hover:bg-gray-100 cursor-pointer flex items-center justify-between"
                      onClick={() => toggleExpand(salesperson.name)}
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`flex items-center justify-center w-10 h-10 rounded-full ${colors.badge}`}>
                          <span className={`font-semibold ${colors.text}`}>
                            #{index + 1}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">
                            {salesperson.name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {formatNumber(salesperson.salesCount)} sales
                            {salesperson.refundCount > 0 && (
                              <span className="text-red-500 ml-1">
                                • {salesperson.refundCount} refund{salesperson.refundCount !== 1 ? 's' : ''}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-6">
                        <div className="text-right hidden md:block">
                          <p className="text-sm text-gray-600">Gross Sales</p>
                          <p className="font-semibold text-gray-900">
                            {formatCurrency(salesperson.totalSales, 'USD')}
                          </p>
                        </div>
                        {salesperson.totalRefunds > 0 && (
                          <div className="text-right hidden lg:block">
                            <p className="text-sm text-gray-600">Refunds</p>
                            <p className="font-semibold text-red-600">
                              -{formatCurrency(salesperson.totalRefunds, 'USD')}
                            </p>
                          </div>
                        )}
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Net Commission</p>
                          <p className="font-semibold text-green-600">
                            {formatCurrency(salesperson.netCommission ?? salesperson.totalCommission, 'USD')}
                          </p>
                          {salesperson.totalRefundCommission > 0 && (
                            <p className="text-xs text-gray-400">
                              (Gross: {formatCurrency(salesperson.totalCommission, 'USD')})
                            </p>
                          )}
                        </div>
                        <div>
                          {expandedSalesperson === salesperson.name ? (
                            <FiChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <FiChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>

                {/* Expanded Sales Details */}
                {expandedSalesperson === salesperson.name && (
                  <div className="p-4 bg-white border-t border-gray-200 space-y-6">
                    {/* Summary for this salesperson */}
                    {salesperson.totalRefunds > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-xs text-gray-500">Gross Sales</p>
                          <p className="font-semibold text-gray-900">{formatCurrency(salesperson.totalSales, 'USD')}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Refunds</p>
                          <p className="font-semibold text-red-600">-{formatCurrency(salesperson.totalRefunds, 'USD')}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Net Sales</p>
                          <p className="font-semibold text-blue-600">{formatCurrency(salesperson.netSales, 'USD')}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Net Commission</p>
                          <p className="font-semibold text-green-600">{formatCurrency(salesperson.netCommission, 'USD')}</p>
                        </div>
                      </div>
                    )}

                    {/* Sales Table */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">
                        Sales ({salesperson.salesCount})
                      </h4>
                      <div className="table-container">
                        <table className="table">
                          <thead>
                            <tr>
                              <th>Type</th>
                              <th>Customer Email</th>
                              <th>Amount</th>
                              <th>Commission</th>
                              <th>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {salesperson.sales.map((sale) => (
                              <tr key={sale.id}>
                                <td>
                                  <span className={`badge ${
                                    sale.type === 'First Payment'
                                      ? 'bg-blue-100 text-blue-800'
                                      : 'bg-purple-100 text-purple-800'
                                  }`}>
                                    {sale.type}
                                  </span>
                                </td>
                                <td className="text-sm">
                                  {sale.customerEmail || 'N/A'}
                                </td>
                                <td className="font-medium">
                                  {formatCurrency(sale.amount, sale.currency)}
                                </td>
                                <td className="font-medium text-green-600">
                                  {formatCurrency(sale.commission, sale.currency)}
                                </td>
                                <td className="text-sm text-gray-600">
                                  {formatDate(sale.date)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Refunds Table */}
                    {salesperson.refunds && salesperson.refunds.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-red-700 mb-3 flex items-center gap-2">
                          <FiRotateCcw className="w-4 h-4" />
                          Refunds ({salesperson.refundCount})
                        </h4>
                        <div className="table-container">
                          <table className="table">
                            <thead>
                              <tr>
                                <th>Reason</th>
                                <th>Customer Email</th>
                                <th>Amount</th>
                                <th>Commission Deducted</th>
                                <th>Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {salesperson.refunds.map((refund) => (
                                <tr key={refund.id} className="bg-red-50">
                                  <td>
                                    <span className="badge bg-red-100 text-red-800">
                                      {refund.reason || 'No reason'}
                                    </span>
                                  </td>
                                  <td className="text-sm">
                                    {refund.customerEmail || 'N/A'}
                                  </td>
                                  <td className="font-medium text-red-600">
                                    -{formatCurrency(refund.amount, refund.currency)}
                                  </td>
                                  <td className="font-medium text-red-600">
                                    -{formatCurrency(refund.commission, refund.currency)}
                                  </td>
                                  <td className="text-sm text-gray-600">
                                    {formatDate(refund.date)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default Commissions;
